import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../state/api.js';

/**
 * Satu bentuk pemuatan untuk seluruh layar konsol.
 *
 * MENGAPA HOOK INI ADA, DAN APA YANG IA CEGAH
 * -------------------------------------------
 * Empat layar pertama masing-masing menulis ulang blok yang sama: `useState` untuk data,
 * `useState` untuk status, `useState` untuk galat, `useCallback` untuk memuat,
 * `useEffect` untuk memanggilnya. Itu lima belas baris per layar yang tidak menyatakan
 * apa pun tentang layarnya — dan lebih buruk: setiap salinan adalah kesempatan
 * melupakan salah satu dari EMPAT keadaan yang peta §5 tuntut untuk SETIAP layar
 * (baku · memuat · kosong · gagal).
 *
 * Yang paling mudah terlupa bukan `loading`. Ia `error`: sebuah layar yang hanya menangani
 * sukses menampilkan daftar kosong ketika jaringan gagal, dan pengguna membaca "belum ada
 * data" atas kegagalan sambungan. Itu bentuk cacat yang sama dengan UAT D-11, dan ia
 * tidak dapat dilihat dari membaca kode layarnya satu per satu.
 *
 * Jadi keempat keadaan dikembalikan sebagai SATU nilai `status` yang tidak dapat
 * setengah: sebuah layar yang lupa merender `error` tidak akan merender apa pun, dan itu
 * terlihat langsung. `Screen` di `components/Screen.tsx` merender ketiga keadaan bukan-siap
 * untuk pemakainya, sehingga lupa pun menghasilkan kondisi yang benar.
 */
export type ResourceStatus = 'loading' | 'ready' | 'error';

export interface Resource<T> {
  status: ResourceStatus;
  data: T | null;
  error: ApiError | null;
  /** Memuat ulang. Dipakai tombol "Coba lagi" dan setelah setiap aksi yang menulis. */
  reload: () => Promise<void>;
}

/**
 * `deps` adalah nilai yang, bila berubah, MEMBATALKAN data yang sudah dimuat — saringan,
 * kode pada alamat, periode. Ia bukan daftar dependensi React: ia daftar hal yang membuat
 * jawaban sebelumnya menjadi jawaban atas pertanyaan yang berbeda.
 */
export function useResource<T>(
  load: () => Promise<T>,
  deps: readonly unknown[] = [],
  options: { enabled?: boolean } = {},
): Resource<T> {
  const enabled = options.enabled ?? true;
  const [data, setData] = useState<T | null>(null);
  const [status, setStatus] = useState<ResourceStatus>(enabled ? 'loading' : 'ready');
  const [error, setError] = useState<ApiError | null>(null);

  const key = JSON.stringify(deps);
  const reload = useCallback(async () => {
    if (!enabled) return;
    setStatus('loading');
    setError(null);
    try {
      setData(await load());
      setStatus('ready');
    } catch (err) {
      /**
       * `ApiError` membawa `Problem` dari server, dan itu yang dirender layar — lewat
       * katalog i18n, dari `problem.code`. `detail_en` TIDAK PERNAH sampai ke layar
       * (ADR T1-06); sebuah galat yang bukan `ApiError` (mis. jaringan mati) menjadi
       * `null`, dan layar menampilkan kalimat galat umumnya.
       */
      setError(err instanceof ApiError ? err : null);
      setStatus('error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { status, data, error, reload };
}
